import React from 'react';

/**
 * Header Component
 *
 * Page header component using the Coord design system.
 * Supports variants, sizes, and optional subtitle.
 *
 * @example
 * <Header title="Dashboard" subtitle="Welcome back" />
 * <Header variant="dashboard" size="lg">Custom content</Header>
 */
const Header = ({
  title,
  subtitle,
  className = '',
  variant = 'default',
  size = 'lg',
  centered = false,
  children
}) => {
  const cn = (...classes) => classes.filter(Boolean).join(' ');

  const variantClasses = {
    default: 'bg-white border-b border-border-light',
    transparent: 'bg-transparent',
    primary: 'bg-primary text-white',
    card: 'bg-background-card',
    dashboard: 'bg-background',
    gradient: 'bg-gradient-warm'
  };

  const sizeClasses = {
    sm: 'py-4 px-4 sm:px-6',
    md: 'py-6 px-4 sm:px-6',
    lg: 'py-8 px-4 sm:px-6 lg:px-8',
    xl: 'py-10 px-4 sm:px-6 lg:px-8'
  };

  const titleSizeClasses = {
    sm: 'text-2xl',
    md: 'text-3xl',
    lg: 'text-4xl',
    xl: 'text-display-md'
  };

  return (
    <header className={cn(
      'w-full transition-all duration-200',
      variantClasses[variant],
      sizeClasses[size],
      className
    )}>
      <div className="max-w-7xl mx-auto">
        {/* Render children if provided, otherwise render title/subtitle */}
        {children ? (
          children
        ) : (
          <div className={cn(
            'flex flex-col animate-fade-in',
            centered && 'text-center'
          )}>
            <h1 className={cn(
              'font-display font-semibold text-text-primary',
              titleSizeClasses[size],
              variant === 'primary' && 'text-white'
            )}>
              {title}
            </h1>
            {subtitle && (
              <p className={cn(
                'text-text-secondary mt-2 text-lg animate-fade-in-up',
                variant === 'primary' && 'text-white/90'
              )}>
                {subtitle}
              </p>
            )}
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
